// Spreadsheet-import themes for /apps/pdf-tools/pdf-editor.
//
// Same contract as md-theme.ts: a theme is a plain serializable object
// holding every style the sheet → PDF layout engine (sheet-layout.ts)
// understands, grouped the way the import screen presents them —
// page, title, columns, rows, cells, header row, page header/footer.
// Presets are starting points; the import view deep-clones one and
// lets the user edit any field, so nothing here is ever mutated.
//
// Font families use the editor's id space ("helvetica" | "times" |
// "courier" | "lib:<id>" | uploaded ids); variants are resolved by the
// import view and shipped to the worker as faces keyed by
// `faceKey(family, bold, italic)` (md-theme.ts).

import type { MdAlign, MdPageStyle } from "./md-theme.ts";

/** How the table divides the frame between columns. */
export type SheetColumnWidthMode =
  /** Measured content widths, shrunk proportionally when too wide. */
  | "auto"
  /** frame ÷ column count. */
  | "equal"
  /** Like auto, but stretched to fill the full frame width. */
  | "fill";

/** Per-cell horizontal alignment. "auto" = numbers/dates right,
 *  booleans centre, text left — the spreadsheet convention. */
export type SheetCellAlign = "auto" | "left" | "center" | "right";

/** Which grid lines are drawn. */
export type SheetGridMode = "all" | "horizontal" | "outline" | "none";

export interface SheetTitleStyle {
  /** Draw a title line above each sheet's table. */
  show: boolean;
  /** What the title says: the sheet tab name or the file name. */
  source: "sheet" | "file";
  family: string;
  size: number;
  color: string;
  bold: boolean;
  italic: boolean;
  align: MdAlign;
  /** Gap between the title baseline block and the table, pt. */
  spaceAfter: number;
  /** Accent rule under the title; null = none. */
  ruleColor: string | null;
}

export interface SheetColumnStyle {
  widthMode: SheetColumnWidthMode;
  align: SheetCellAlign;
}

export interface SheetRowStyle {
  /** Every-other-row tint; null = none. */
  stripeBackground: string | null;
  /** Drop rows whose every cell is empty. */
  skipBlank: boolean;
}

export interface SheetCellStyle {
  family: string;
  size: number;
  color: string;
  /** Baseline-to-baseline distance as a multiple of size. */
  lineHeight: number;
  /** Inner padding, pt. */
  paddingX: number;
  paddingY: number;
  /** Wrap long cells onto multiple lines; off = clip with an ellipsis. */
  wrap: boolean;
}

export interface SheetGridStyle {
  mode: SheetGridMode;
  color: string;
  /** Line thickness, pt. */
  width: number;
}

export interface SheetHeaderStyle {
  /** Treat the first data row as a header row. */
  enabled: boolean;
  /** Redraw the header at the top of every page the table spans. */
  repeat: boolean;
  background: string;
  color: string;
  bold: boolean;
  italic: boolean;
  /** Header font size, pt (independent of the cell size). */
  size: number;
  /** Heavier rule under the header band; null = grid only. */
  ruleColor: string | null;
}

/** Running page header / footer line. `text` supports the
 *  placeholders {page} {pages} {sheet} {file} {date}; "" = off. */
export interface SheetPageTextStyle {
  text: string;
  align: MdAlign;
  size: number;
  color: string;
}

export interface SheetThemeConfig {
  page: MdPageStyle;
  title: SheetTitleStyle;
  columns: SheetColumnStyle;
  rows: SheetRowStyle;
  cells: SheetCellStyle;
  grid: SheetGridStyle;
  header: SheetHeaderStyle;
  pageHeader: SheetPageTextStyle;
  pageFooter: SheetPageTextStyle;
}

export interface SheetThemePreset {
  id: string;
  label: string;
  /** One-line description shown under the label in the gallery. */
  blurb: string;
  config: SheetThemeConfig;
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** The shared skeleton presets are built from — “Clean” verbatim.
 *  Spreadsheets default to landscape: real-world sheets are wide. */
const CLEAN: SheetThemeConfig = {
  page: {
    preset: "A4",
    landscape: true,
    marginTop: 48,
    marginRight: 44,
    marginBottom: 48,
    marginLeft: 44,
    background: "#ffffff",
  },
  title: {
    show: true,
    source: "sheet",
    family: "helvetica",
    size: 16,
    color: "#1a1a1a",
    bold: true,
    italic: false,
    align: "left",
    spaceAfter: 10,
    ruleColor: null,
  },
  columns: { widthMode: "auto", align: "auto" },
  rows: { stripeBackground: null, skipBlank: false },
  cells: {
    family: "helvetica",
    size: 9,
    color: "#24292f",
    lineHeight: 1.3,
    paddingX: 6,
    paddingY: 4,
    wrap: true,
  },
  grid: { mode: "all", color: "#d0d7de", width: 0.75 },
  header: {
    enabled: true,
    repeat: true,
    background: "#f6f8fa",
    color: "#1f2328",
    bold: true,
    italic: false,
    size: 9,
    ruleColor: null,
  },
  pageHeader: { text: "", align: "left", size: 8, color: "#57606a" },
  pageFooter: {
    text: "Page {page} of {pages}",
    align: "center",
    size: 8,
    color: "#57606a",
  },
};

/** Deep-merge helper for preset variations. */
function themed(patch: {
  page?: Partial<MdPageStyle>;
  title?: Partial<SheetTitleStyle>;
  columns?: Partial<SheetColumnStyle>;
  rows?: Partial<SheetRowStyle>;
  cells?: Partial<SheetCellStyle>;
  grid?: Partial<SheetGridStyle>;
  header?: Partial<SheetHeaderStyle>;
  pageHeader?: Partial<SheetPageTextStyle>;
  pageFooter?: Partial<SheetPageTextStyle>;
}): SheetThemeConfig {
  const base = clone(CLEAN);
  return {
    page: { ...base.page, ...patch.page },
    title: { ...base.title, ...patch.title },
    columns: { ...base.columns, ...patch.columns },
    rows: { ...base.rows, ...patch.rows },
    cells: { ...base.cells, ...patch.cells },
    grid: { ...base.grid, ...patch.grid },
    header: { ...base.header, ...patch.header },
    pageHeader: { ...base.pageHeader, ...patch.pageHeader },
    pageFooter: { ...base.pageFooter, ...patch.pageFooter },
  };
}

export const SHEET_THEMES: readonly SheetThemePreset[] = [
  {
    id: "clean",
    label: "Clean",
    blurb: "Neutral grid, grey header — the safe default",
    config: clone(CLEAN),
  },
  {
    id: "ledger",
    label: "Ledger",
    blurb: "Striped rows, horizontal rules only",
    config: themed({
      rows: { stripeBackground: "#f6f8fa" },
      grid: { mode: "horizontal", color: "#d8dee4" },
      header: { background: "#ffffff", ruleColor: "#8c959f" },
      title: { ruleColor: "#d0d7de" },
    }),
  },
  {
    id: "corporate",
    label: "Corporate",
    blurb: "Navy header band, zebra body, sans-serif",
    config: themed({
      cells: { family: "lib:opensans", size: 8.5 },
      title: { family: "lib:opensans", color: "#1e3a5f" },
      header: {
        background: "#1e3a5f",
        color: "#ffffff",
        size: 8.5,
        ruleColor: null,
      },
      rows: { stripeBackground: "#eef2f7" },
      grid: { mode: "horizontal", color: "#c9d4e0" },
      pageFooter: {
        text: "{file} · page {page} of {pages}",
        align: "right",
        color: "#5b6b7b",
      },
    }),
  },
  {
    id: "minimal",
    label: "Minimal",
    blurb: "No fills, hairline rules, lots of air",
    config: themed({
      cells: { paddingX: 8, paddingY: 6 },
      title: { bold: false, size: 18, color: "#111111" },
      header: {
        background: "#ffffff",
        color: "#111111",
        ruleColor: "#111111",
      },
      grid: { mode: "horizontal", color: "#e5e5e5", width: 0.5 },
      pageFooter: { text: "{page}", align: "center", color: "#a3a3a3" },
    }),
  },
  {
    id: "print",
    label: "Print",
    blurb: "Compact mono-friendly grid — more per page",
    config: themed({
      page: { marginTop: 36, marginRight: 32, marginBottom: 36, marginLeft: 32 },
      title: { size: 12, spaceAfter: 6 },
      cells: { size: 7.5, paddingX: 4, paddingY: 2.5, lineHeight: 1.25 },
      header: { size: 7.5, background: "#eeeeee" },
      grid: { color: "#bbbbbb", width: 0.5 },
      pageHeader: { text: "{file} — {sheet}", align: "left" },
      pageFooter: { text: "{date} · page {page} of {pages}", align: "right" },
    }),
  },
  {
    id: "warm",
    label: "Warm",
    blurb: "Serif body, amber header, soft stripes",
    config: themed({
      page: { landscape: false },
      cells: { family: "lib:libserif", size: 9.5 },
      title: { family: "lib:libserif", color: "#7c2d12", ruleColor: "#d97706" },
      header: {
        background: "#fef3c7",
        color: "#7c2d12",
        ruleColor: "#d97706",
      },
      rows: { stripeBackground: "#fffbeb" },
      grid: { mode: "horizontal", color: "#e7d8b0" },
      pageFooter: { text: "Page {page} of {pages}", color: "#92400e" },
    }),
  },
];

export const cloneSheetTheme = (c: SheetThemeConfig): SheetThemeConfig => clone(c);

/** Every family the theme can draw with (face resolution). */
export function sheetThemeFamilies(c: SheetThemeConfig): string[] {
  return [...new Set([c.cells.family, c.title.family])];
}
